import type { FC, ReactElement } from 'react';
import React from 'react';

import { Provider } from 'react-redux';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import ApplicationInsightsProvider from '@framework/appInsights';

import useRegisterEventReporting from '@hooks/useRegisterEventReporting';

import initializeLoginStore from '@redux/appStore/loginStore';

import Content from '@accounts/components/layout/Content/Content';
import Footer from '@accounts/components/layout/Footer/Footer';
import InnerContainer from '@accounts/components/layout/InnerContainer/InnerContainer';
import PageContainer from '@accounts/components/layout/PageContainer/PageContainer';
import LanguageSwitcher from '@accounts/components/ui/LanguageSwitcher/LanguageSwitcher';

import Container from '@components/layout/Container/Container';
import Application from '@components/pages/other/Application';
import type { RuntimeConfig } from '@components/pages/other/RuntimeContext/Runtime.context';
import Snackbars from '@components/ui/Snackbars/Snackbars';

import styles from './LoginApp.module.scss';

const createQueryClient = (refetchOnWindowFocus?: boolean) =>
    new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus
            }
        }
    });

type LoginAppProps = {
    Component: FC;
    pageProps: any;
    runtimeConfig: RuntimeConfig;
};

export const loginStore = initializeLoginStore();

const LoginApp = ({
    Component: PageComponent,
    pageProps,
    runtimeConfig
}: LoginAppProps): ReactElement => {
    useRegisterEventReporting();

    const { APPLICATION_INSIGHTS_ENABLED, REFETCH_ON_WINDOW_FOCUS } = runtimeConfig;
    const [queryClient] = React.useState(() => createQueryClient(REFETCH_ON_WINDOW_FOCUS));

    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={loginStore}>
                <ApplicationInsightsProvider
                    config={{
                        isEnabled: Boolean(APPLICATION_INSIGHTS_ENABLED),
                        trackPageView: true
                    }}
                    appView="members"
                >
                    <PageContainer>
                        <InnerContainer>
                            <Container className={styles.languageSwitcherContainer}>
                                <LanguageSwitcher />
                            </Container>
                            <Container className={styles.contentContainer}>
                                <Content>
                                    <PageComponent {...pageProps} />
                                </Content>
                            </Container>
                            <Footer />
                        </InnerContainer>
                    </PageContainer>
                    <Snackbars />
                    <Application />
                </ApplicationInsightsProvider>
            </Provider>
        </QueryClientProvider>
    );
};

export default LoginApp;
